import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { Formik, FormikErrors, FormikProps } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { faPineapple } from "@fortawesome/pro-regular-svg-icons";
import { Modal } from "@mui/material";
import { useHistory } from "react-router-dom";
import { themePalette } from "../../theme";
import { Button } from "../shared/Button/Button";
import {
  ContactUsButtonContainer,
  ContactUsFormGrid,
  ContactUsInput,
} from "./ContactUs.styles";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { validateEmail } from "../../utils/hooks/helpers";
import ContactUsRequest from "./Services/ContactUsService";
import { getUserAuth, getUser } from "../../recoil/selectors";
import { ContactUsEmailSerializer } from "../../recoil/types";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import {
  ConfirmationModalMessageWrapper,
  ConfirmationModalContentWrapper,
} from "../shared/Modal/ConfirmationModal.styles";
import { HELP_PAGE_URL } from "../../utils/urlConstants";

interface ContactUsFormValues {
  firstName: string;
  lastName: string;
  phone?: string;
  email: string;
  inquiryType: NonNullable<ContactUsEmailSerializer["inquiry_type"]> | "";
  message: string;
}

export const ContactUsContent = (): React.ReactElement => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const auth = useRecoilValue(getUserAuth);
  const user = useRecoilValue(getUser);

  const getLoadingOrMessage = () => {
    if (isLoading) {
      return <SpinnerLoading />;
    }
  };

  const SuccessMessage = (
    <>
      <FontAwesomeIcon
        icon={faCircleCheck}
        color={themePalette.greenSuccess}
        size="2x"
      />
      <div style={{ margin: "24px 0px" }}>
        <b>{t("contactUs.form.success.title")}</b>
        <p>{t("contactUs.form.success.message")}</p>
        <Button
          label={t("contactUs.form.ok")}
          data-testid="ok-button"
          onClick={() => {
            setIsConfirmed(false);
            history.push(HELP_PAGE_URL);
          }}
          fullWidth
          size="medium"
          backgroundColor="greenDark"
        />
      </div>
    </>
  );

  const ErrorMessage = (
    <>
      <FontAwesomeIcon
        icon={faPineapple}
        color={themePalette.redDark}
        size="2x"
      />
      <div style={{ margin: "24px 0px", color: themePalette.redDark }}>
        <b>{t("contactUs.form.error.title")}</b>
        <p>{t("contactUs.form.error.message")}</p>
        <Button
          label={t("contactUs.form.ok")}
          data-testid="ok-button"
          onClick={() => {
            closeModal();
          }}
          fullWidth
          size="medium"
          backgroundColor="greyMedium"
        />
      </div>
    </>
  );

  const closeModal = () => {
    setIsConfirmed(false);
    setIsError(false);
  };

  return (
    <>
      <Modal
        open={isConfirmed || isError}
        onClose={() => {
          closeModal();
        }}
        style={{
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
        }}
      >
        <ConfirmationModalMessageWrapper deviceType={deviceType}>
          <ConfirmationModalContentWrapper>
            {!isError ? SuccessMessage : ErrorMessage}
          </ConfirmationModalContentWrapper>
        </ConfirmationModalMessageWrapper>
      </Modal>
      <Formik<ContactUsFormValues>
        initialValues={{
          firstName: user?.first_name || "",
          lastName: user?.last_name || "",
          email: auth?.email || "",
          inquiryType: "",
          message: "",
          phone: user?.phone_number || "",
        }}
        validate={(values) => {
          const errors: FormikErrors<ContactUsFormValues> = {};
          Object.keys(values)
            .filter(
              (value) =>
                value !== "phone" &&
                !values[value as keyof ContactUsFormValues],
            )
            .forEach((value) => {
              errors[value as keyof ContactUsFormValues] = t(
                "contactUs.form.errors.required",
              );
            });

          if (values.email && !validateEmail(values.email)) {
            errors.email = t("contactUs.form.errors.invalidEmail");
          }
          return errors;
        }}
        onSubmit={async (values, { resetForm }) => {
          setIsLoading(true);
          try {
            const response = await ContactUsRequest(
              {
                first_name: values.firstName,
                last_name: values.lastName,
                phone: values.phone || "",
                email: values.email,
                message: values.message,
                customer_type: "patient",
                inquiry_type: values.inquiryType || undefined,
              },
              auth?.access_token,
            );
            setIsLoading(false);
            if (response?.ok) {
              setIsConfirmed(true);
              resetForm();
            } else {
              setIsError(true);
            }
          } catch (e) {
            setIsError(true);
            setIsLoading(false);
          }
        }}
        validateOnChange={false}
      >
        {({
          values,
          errors,
          handleSubmit,
          setFieldValue,
          setFieldError,
        }: FormikProps<ContactUsFormValues>) => (
          <ContactUsFormGrid deviceType={deviceType}>
            <ContactUsInput
              select
              required
              id="contact-inquiry-type"
              name="inquiryType"
              value={values.inquiryType}
              onChange={(event) => {
                setFieldError("inquiryType", "");
                setFieldValue("inquiryType", event.target.value);
              }}
              label={t("contactUs.form.fields.inquiryType")}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              size="small"
              error={Boolean(errors.inquiryType)}
              helperText={errors.inquiryType}
            >
              <option value="" disabled>
                {t("contactUs.form.inquiryTypes.placeholder")}
              </option>
              <option value="billing">
                {t("contactUs.form.inquiryTypes.billing")}
              </option>
              <option value="genetic_counseling">
                {t("contactUs.form.inquiryTypes.geneticCounseling")}
              </option>
              <option value="test_ordering">
                {t("contactUs.form.inquiryTypes.testOrdering")}
              </option>
              <option value="other">
                {t("contactUs.form.inquiryTypes.other")}
              </option>
            </ContactUsInput>
            <ContactUsInput
              type="text"
              name="firstName"
              value={values.firstName}
              onChange={(event) => {
                setFieldError("firstName", "");
                setFieldValue("firstName", event.target.value);
              }}
              placeholder={t("contactUs.form.fields.firstName")}
              label={t("contactUs.form.fields.firstName")}
              size="small"
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
            />
            <ContactUsInput
              type="text"
              name="lastName"
              value={values.lastName}
              onChange={(event) => {
                setFieldError("lastName", "");
                setFieldValue("lastName", event.target.value);
              }}
              label={t("contactUs.form.fields.lastName")}
              placeholder={t("contactUs.form.fields.lastName")}
              size="small"
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
            />
            <ContactUsInput
              type="email"
              name="email"
              value={values.email}
              onChange={(event) => {
                setFieldError("email", "");
                setFieldValue("email", event.target.value);
              }}
              label={t("contactUs.form.fields.email")}
              placeholder={t("contactUs.form.fields.email")}
              size="small"
              error={Boolean(errors.email)}
              helperText={errors.email}
            />
            <ContactUsInput
              type="text"
              name="phone"
              value={values.phone}
              onChange={(event) => setFieldValue("phone", event.target.value)}
              label={t("contactUs.form.fields.phoneOptional")}
              placeholder={t("contactUs.form.fields.phoneOptional")}
              size="small"
            />
            <ContactUsInput
              type="text"
              name="message"
              value={values.message}
              onChange={(event) => {
                setFieldError("message", "");
                setFieldValue("message", event.target.value);
              }}
              label={t("contactUs.form.fields.message")}
              placeholder={t("contactUs.form.fields.message")}
              size="small"
              multiline
              rows={5}
              error={Boolean(errors.message)}
              helperText={errors.message}
            />
            {getLoadingOrMessage() || (
              <ContactUsButtonContainer>
                <Button
                  type="submit"
                  label={
                    isLoading ? "..." : t("contactUs.form.buttons.sendInquiry")
                  }
                  size="small"
                  sx={{ textTransform: "uppercase" }}
                  onClick={() => handleSubmit()}
                  disabled={isLoading}
                />
              </ContactUsButtonContainer>
            )}
          </ContactUsFormGrid>
        )}
      </Formik>
    </>
  );
};
